/**
 * AgentPersona — die EINE Definition einer Realtime-Figur.
 *
 * Ziel (Alex 2026-08-03): mehrere Agenten sollen sich schnell und sauber
 * anlegen lassen — durch Vererbung, nicht durch Kopieren einer weiteren
 * if/else-Verzweigung im Root. Vorher entschied `TschaukoAgentRoot` per
 * `getConfig().id === 'udo' ? … : …`, welche Instruktionen, Begrüßung,
 * Werkzeuge und Stimme gelten; jede neue Figur hätte diese Verzweigung
 * verbreitert und den Root mit Figurenwissen belastet.
 *
 * Aufbau (Template Method, wie im Architecture-Soul-Stil des Projekts):
 *
 *   AgentPersona (abstrakt)
 *     ├─ verlangt:  id, displayName, instructions(), greeting()
 *     ├─ bietet an: tools(), voice(), avatar(), contextLine(),
 *     │             language(), resolveEntryContext()  — alle mit
 *     │             vernünftigen Vorgaben, gezielt überschreibbar
 *     └─ FINAL:     toControllerConfig() — die einzige Stelle, die weiß,
 *                   wie aus einer Figur eine VoiceAgentConfig wird
 *
 * Eine neue Figur ist damit eine Klasse mit vier Zeilen Pflichtangaben;
 * alles Weitere erbt sie. Der Controller selbst bleibt unangetastet — er
 * ist der Motor, die Persona nur sein Datenblatt.
 */

import type { OwlCharacter, OwlSpeechCue } from '../../../components/KeyedOwlAvatar'
import type { ArcturianAvatarVariant } from '../ArcturianAvatarVariant'
import type { GuideEntryContextResolver } from '../GuideEntryContext'
import type { VoiceAgentConfig } from '../VoiceAgentController'
import type { CommandRegistry } from '../types'

/** Wie die Figur auf einer Vollbild-Bühne dargestellt wird. */
export type AgentAvatarStage =
  /** Die freigestellten Menschen-Videos (Brustbild/Ganzkörper). */
  | { kind: 'arcturian'; variant: ArcturianAvatarVariant | 'selectable' }
  /**
   * Die chroma-gekeyten Eulen-Clips (Story/Annotationen teilen sie).
   *
   * `portraitZoom` und `speechCue` gehoeren zur FIGUR, nicht zur Buehne
   * (Alex 2026-08-04: „bei der Steuerung von unterschiedlichen
   * Avatar-Portraets"): eine kleine Eule darf naeher herangeholt werden als
   * eine grosse, und ob Woelkchen zu ihr passen, entscheidet ihr Wesen.
   */
  | { kind: 'owl'; character: OwlCharacter; portraitZoom?: number; speechCue?: OwlSpeechCue }

/** Sprachauflösung pro Sitzung — identisch zur Controller-Signatur. */
export type AgentLanguageResolver = () => { directive: string; greeting: string; name: string }

/** Was der Host über eine Figur wissen muss, ohne sie zu kennen. */
export interface AgentPersonaDescriptor {
  id: string
  displayName: string
  avatar: AgentAvatarStage
}

export abstract class AgentPersona {
  /** Stabile Kennung (Route, Speicher, Telemetrie). */
  abstract readonly id: string
  /** Anzeigename in Status, Untertitel, Auswahl. */
  abstract readonly displayName: string

  /** Der Systemtext der Figur. */
  protected abstract instructions(): string
  /** Der eine Begrüßungssatz zum Sitzungsstart. */
  protected abstract greeting(): string

  // ── Überschreibbare Vorgaben ──────────────────────────────────────────

  /** Werkzeuge (Realtime function calling). Ohne Werkzeuge: reine Stimme. */
  protected tools(): ReadonlyArray<Record<string, unknown>> { return [] }

  /** Realtime-Stimme des Anbieters. */
  protected voice(): string { return 'verse' }

  /** Bühnendarstellung. Vorgabe ist das opake Brustbild. */
  avatar(): AgentAvatarStage { return { kind: 'arcturian', variant: 'midshot' } }

  /** Sitzungs-Kontextzeile (z. B. GPS-Standort, Fahrplan). */
  protected contextLine(): (() => Promise<string>) | undefined { return undefined }

  /** Sprachwahl zur Laufzeit; ohne Auflösung bleibt die Figur einsprachig. */
  protected language(): AgentLanguageResolver | undefined { return undefined }

  /** Vertrauenswürdige Faktenauflösung für kontextuelle Einstiege (#842). */
  protected resolveEntryContext(): GuideEntryContextResolver | undefined { return undefined }

  /** Aktuelle GPS-Position, falls die Figur ortsbezogen arbeitet. */
  protected position(): (() => { lat: number; lon: number } | null) | undefined { return undefined }

  /** Sitzungslänge in Sekunden. */
  protected maxSessionSeconds(): number { return 600 }

  // ── Template Method ───────────────────────────────────────────────────

  /**
   * Baut die Controller-Konfiguration. BEWUSST nicht überschreibbar: Wer
   * eine Figur anlegt, beantwortet Fragen — er verdrahtet keinen Motor.
   * Kommt eine neue Controller-Option dazu, wird sie hier EINMAL ergänzt
   * und steht sofort allen Figuren zur Verfügung.
   */
  readonly toControllerConfig = (deps: {
    registry: CommandRegistry
    onError?: (event: string, err: unknown, ctx?: Record<string, unknown>) => void
    onInfo?: (event: string, ctx?: Record<string, unknown>) => void
  }): VoiceAgentConfig => ({
    instructions: this.instructions(),
    greeting: this.greeting(),
    tools: this.tools(),
    voice: this.voice(),
    maxSessionSeconds: this.maxSessionSeconds(),
    registry: deps.registry,
    getContextLine: this.contextLine(),
    getLanguage: this.language(),
    resolveEntryContext: this.resolveEntryContext(),
    getPosition: this.position(),
    onError: deps.onError,
    onInfo: deps.onInfo,
  } as VoiceAgentConfig)

  /** Schlanke Beschreibung für Hosts (Bühne, Name) ohne Figurenwissen. */
  readonly describe = (): AgentPersonaDescriptor => ({
    id: this.id,
    displayName: this.displayName,
    avatar: this.avatar(),
  })
}
